import type { FC } from 'react';

import ListingStatusPill from '@/common-components/ListingStatusPill';
import Pill from '@/common-components/Pill';
import { CATEGORY_META } from '@/constants/listing.constants';
import { formatRupees } from '@/utils/currency.util';
import type { AdminListingListItem } from '@/types/rental.types';

interface ListingCardProps {
  listing: AdminListingListItem;
  mutating: boolean;
  onView: (listing: AdminListingListItem) => void;
  onRemove: (listing: AdminListingListItem) => void;
  onRestore: (listing: AdminListingListItem) => void;
}

const ListingCard: FC<ListingCardProps> = ({ listing, mutating, onView, onRemove, onRestore }) => {
  const meta = CATEGORY_META[listing.category];
  const isRemoved = listing.status === 'removed';
  const isReported = listing.reportCount > 0;

  const actionBtn =
    'rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div
      className="flex flex-col overflow-hidden rounded-2xl border bg-white shadow-[var(--shadow-card)] transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-hover)]"
      style={{ borderColor: isReported ? 'var(--color-danger)' : 'var(--color-divider)' }}
    >
      {/* Image */}
      <div
        className="flex h-[140px] items-center justify-center text-5xl"
        style={{ background: meta.gradient }}
      >
        {listing.photos.length > 0 ? (
          <img src={listing.photos[0]} alt={listing.title} className="h-full w-full object-cover" />
        ) : (
          <span>{meta.icon}</span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-1 flex items-start justify-between gap-2">
          <h3 className="text-[14px] font-bold leading-snug text-[var(--color-ink)]">
            {listing.title}
          </h3>
          <ListingStatusPill status={listing.status} />
        </div>
        <div className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-[var(--color-ink-muted)]">
          <span>📍 {listing.campusName}</span>
          <span>by {listing.ownerName}</span>
          {listing.ratingCount > 0 ? <span>⭐ {listing.ratingAvg.toFixed(1)}</span> : null}
        </div>
        <div className="mt-auto text-[17px] font-extrabold text-brand-500">
          {formatRupees(listing.pricePerDayPaise)}
          <span className="text-[12px] font-medium text-[var(--color-ink-muted)]"> /day</span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-[var(--color-divider)] px-4 py-3">
        <div className="text-[11.5px] text-[var(--color-ink-muted)]">
          {isReported ? (
            <span className="font-semibold text-[var(--color-danger-ink)]">
              ⚠️ {listing.reportCount} report{listing.reportCount === 1 ? '' : 's'}
            </span>
          ) : listing.timesRented > 0 ? (
            <span>📦 Rented {listing.timesRented} time{listing.timesRented === 1 ? '' : 's'}</span>
          ) : (
            <span>📦 New listing</span>
          )}
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onView(listing)}
            className={`${actionBtn} border-[var(--color-divider)] bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)] hover:border-brand-500 hover:bg-brand-50 hover:text-brand-600`}
          >
            View
          </button>
          {isRemoved ? (
            <button
              type="button"
              disabled={mutating}
              onClick={() => onRestore(listing)}
              className={`${actionBtn} border-[var(--color-success)] bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white`}
            >
              {mutating ? '…' : 'Restore'}
            </button>
          ) : (
            <button
              type="button"
              disabled={mutating}
              onClick={() => onRemove(listing)}
              className={`${actionBtn} border-[var(--color-danger)] bg-[var(--color-danger-bg)] text-[var(--color-danger-ink)] hover:bg-[var(--color-danger)] hover:text-white`}
            >
              {mutating ? '…' : 'Remove'}
            </button>
          )}
        </div>
      </div>

      {isReported && !isRemoved ? (
        <div className="border-t border-[var(--color-divider)] px-4 pb-3 pt-2">
          <Pill tone="danger">Under review</Pill>
        </div>
      ) : null}
    </div>
  );
};

export default ListingCard;
